import React, { useState } from "react";
import { getRecaptchaToken } from "../../lib/recaptcha";
import { logAnalyticsEvent } from "../../lib/analytics";

type State = "idle" | "submitting" | "success" | "error";

export interface ContactFormProps {
  /** Shown as the default inquiry type badge, and sent as `inquiry_type`. */
  inquiryType?: "funding" | "engineering" | "advisory" | "general";
  heading?: string;
  successMessage?: string;
  /**
   * If set, the form submits `wish_item_id` so the notify email is tagged
   * "Directed gift intent: <name>" and TK can reconcile against the wire.
   */
  wishItemId?: string;
  /** Human-readable name; surfaces in the notify subject if provided. */
  wishItemName?: string;
}

/**
 * ContactForm — posts name/email/message to /api/contact. Sends an
 * acknowledgement to the sender and a notification to the Foundation.
 */
export function ContactForm({
  inquiryType = "general",
  heading = "Get in touch",
  successMessage = "Thank you. Your message is with us, and a confirmation is on its way to your inbox.",
  wishItemId,
  wishItemName,
}: ContactFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // Honeypot field — must remain empty. Bots that auto-fill every input will
  // populate this and be silently rejected server-side.
  const [company, setCompany] = useState("");
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<string>("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === "submitting") return;
    setState("submitting");
    setError("");
    try {
      const recaptchaToken = await getRecaptchaToken("contact");
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          inquiry_type: inquiryType,
          company,
          recaptchaToken,
          ...(wishItemId
            ? { wish_item_id: wishItemId, wish_item_name: wishItemName }
            : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setState("success");
      setName("");
      setEmail("");
      setMessage("");
      void logAnalyticsEvent("contact_submit", {
        result: "success",
        inquiry_type: inquiryType,
      });
    } catch (err: unknown) {
      setState("error");
      const errorMessage =
        err instanceof Error ? err.message : "unknown_error";
      setError(
        err instanceof Error
          ? `Something went wrong: ${err.message}`
          : "Something went wrong. Please try again shortly."
      );
      void logAnalyticsEvent("contact_submit", {
        result: "error",
        inquiry_type: inquiryType,
        error: errorMessage,
      });
    }
  }

  if (state === "success") {
    return (
      <div className="border border-hairline bg-paper-2 p-6 font-serif text-ink-2">
        {successMessage}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" aria-live="polite">
      <h2 className="text-2xl font-serif text-ink">{heading}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1">
            Name
          </label>
          <input
            id="contact-name"
            name="name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1">
            Email
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-message" className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1">
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
        />
      </div>
      <input type="hidden" name="inquiry_type" value={inquiryType} />
      {/* Honeypot — visually hidden, off the tab order, ignored by humans. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="contact-company">Company</label>
        <input
          id="contact-company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>
      <button
        type="submit"
        disabled={state === "submitting"}
        className="bg-ink text-paper px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-ink-2 transition-colors disabled:opacity-60"
      >
        {state === "submitting" ? "Sending\u2026" : "Send"}
      </button>
      {state === "error" && (
        <p className="text-xs font-mono text-state-pending">{error}</p>
      )}
    </form>
  );
}
